import { useEffect, useRef } from 'react'
import { CONNECTION_RESTORED_EVENT } from '../lib/api'

// Issue #759 (final review F7): ConnectionStatusBanner tells the user "we'll reconnect
// automatically", but previously nothing actually probed the server while the tab stayed open and
// focused - the connection only got re-checked if the user manually refreshed, or switched away
// from the tab and back (UserContext's visibilitychange/focus recheck). This hook makes that
// banner copy literally true: while disconnected and the tab is focused, it probes a lightweight
// endpoint on a fixed interval and dispatches the same CONNECTION_RESTORED_EVENT the rest of the
// app already listens for (useConnectionStatus) the moment the server is reachable again.
//
// Deliberately bounded (RECONNECT_PROBE_MAX_ATTEMPTS) rather than retrying forever: if the user's
// connection is down for an extended period, the probe should not keep hitting the server
// indefinitely in the background. Automatic probing resumes with a fresh budget the next time the
// connection is reported lost (including via UserContext's own focus/visibilitychange recheck, or
// any other surface that dispatches CONNECTION_LOST_EVENT) - but see RECONNECT_PROBE_RESET_WINDOW_MS
// below for why that "fresh budget" is itself time-bounded, not unconditional.
export const RECONNECT_PROBE_INTERVAL_MS = 5000
export const RECONNECT_PROBE_MAX_ATTEMPTS = 12
// Security review correction (M2): the server has no side-effect-free health endpoint - an
// unauthenticated /csrfToken probe creates an orphan session on every request (see
// ShieldUnlessLearnHost.ts), so the probe uses /me with the existing session cookie instead, like
// UserContext's own recheck. A second of random jitter
// on top of the fixed interval avoids every disconnected tab in a thundering herd hitting the
// server on the exact same tick.
export const RECONNECT_PROBE_JITTER_MS = 1000
// Security review correction (M3): resetting the attempt budget to 0 on *every* reconnect lets a
// rapidly flapping connection rack up effectively unbounded total probes over time, defeating the
// bound's purpose. A fresh budget is only granted if the previous disconnected spell's probing
// window has fully elapsed; a reconnect inside that window just pauses the same budget instead of
// renewing it.
export const RECONNECT_PROBE_RESET_WINDOW_MS = 5 * 60 * 1000

function useReconnectProbe(isConnected: boolean) {
    const attemptsRef = useRef(0)
    const windowStartRef = useRef<number | null>(null)

    useEffect(() => {
        if (isConnected) {
            return
        }

        // Issue #857: performance.now() is monotonic - immune to the system wall clock being
        // stepped. Date.now() is not: a backward step is harmless (just conservative - no early
        // reset), but a forward step of more than the reset window would have granted one
        // early/unearned attempt-budget reset.
        const now = performance.now()
        if (windowStartRef.current === null || now - windowStartRef.current >= RECONNECT_PROBE_RESET_WINDOW_MS) {
            windowStartRef.current = now
            attemptsRef.current = 0
        }

        let timer: ReturnType<typeof setTimeout> | null = null
        // Set by cleanup so an in-flight probe cannot re-arm a timer or dispatch an event for an
        // effect that has already been torn down (e.g. connection restored by another request).
        let disposed = false
        const controller = new AbortController()

        // Mutually-recursive helpers - declared as hoisted function declarations (not const arrow
        // functions) so each can reference the other regardless of source order.
        function scheduleNext() {
            if (disposed) {
                return
            }
            if (attemptsRef.current >= RECONNECT_PROBE_MAX_ATTEMPTS) {
                // Security review correction (6th round, residual F-2 sliver): this terminate
                // path must cancel any still-pending, timer-driven handle too - not just discard
                // this closure's reference to it - otherwise a racing focus-triggered probe that
                // happens to be the one which exhausts the budget leaves that handle armed and
                // unreachable by the effect's own cleanup, ready to resume firing uncontrolled
                // once a later budget reset makes it live again.
                if (timer) {
                    clearTimeout(timer)
                }
                timer = null
                return
            }
            // Security review correction (5th round, F-2): clear any previously pending timer
            // before assigning a new one. Without this, a focus-triggered probe (which calls this
            // same function from its own finally{} block) would silently overwrite - not cancel -
            // a still-pending, regularly-scheduled timer handle, orphaning it: that leaked timer
            // keeps firing fetches later, surviving both reconnect and the effect's own cleanup
            // (which only ever clears the single, latest handle this variable happens to hold).
            if (timer) {
                clearTimeout(timer)
            }
            timer = setTimeout(probe, RECONNECT_PROBE_INTERVAL_MS + Math.random() * RECONNECT_PROBE_JITTER_MS)
        }

        async function probe() {
            if (disposed) {
                return
            }
            // Security review correction (5th round, F-1): this budget check previously lived
            // only in scheduleNext(), so the window `focus` listener below - which calls probe()
            // directly, not through scheduleNext() - could mint another probe (and another fetch)
            // even after the attempt budget was already exhausted; a user simply refocusing the
            // tab was enough, no attacker required.
            if (attemptsRef.current >= RECONNECT_PROBE_MAX_ATTEMPTS) {
                return
            }
            if (!document.hasFocus()) {
                // Issue #857: an unfocused tab stops its timer rather than re-arming it on every
                // tick forever; the window `focus` listener below resumes probing on return.
                if (timer) {
                    clearTimeout(timer)
                }
                timer = null
                return
            }
            attemptsRef.current += 1
            try {
                // Mirrors UserContext's own recheck(): a raw, manually-dispatched-event fetch, not
                // lib/api.ts's request() helper - reaching the server at all (any status) is
                // sufficient proof the connection is back, and this probe has no session-expiry/
                // body-parsing concerns of its own to layer on top.
                //
                // Credentials are included so the probe reuses the existing session rather than
                // minting an orphan one per attempt; any status (including 401) proves reachability.
                await fetch(`${process.env.REACT_APP_BASE_API}/me`, { credentials: 'include', signal: controller.signal })
                if (disposed) {
                    return
                }
                window.dispatchEvent(new Event(CONNECTION_RESTORED_EVENT))
            } catch (_) {
                // Still unreachable - scheduleNext() below retries, up to the bound.
            } finally {
                scheduleNext()
            }
        }

        scheduleNext()
        // Probe immediately on refocus too, so reconnection is detected promptly rather than
        // waiting out the rest of the current interval tick.
        window.addEventListener('focus', probe)

        return () => {
            disposed = true
            controller.abort()
            if (timer) {
                clearTimeout(timer)
            }
            window.removeEventListener('focus', probe)
        }
    }, [isConnected])
}

export default useReconnectProbe
